import { Component, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { SyslogRow, SyslogService } from '../services/syslog.service';
import { ToastrService } from 'ngx-toastr';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LiveSyslogHelpComponent } from './live-syslog-help/live-syslog-help.component';

@Component({
    selector: 'dcc-live-syslog',
    templateUrl: './live-syslog.component.html',
    styleUrls: ['./live-syslog.component.scss']
})
export class LiveSyslogComponent implements AfterViewChecked {
    @ViewChild('viewer') private viewer: ElementRef;

    serialNumber = '';
    lineCount = 100;
    wordWrap = true;
    logs: SyslogRow[] = [];
    loading = false;
    requesting = false;
    cooldown = 0;
    private shouldScrollToBottom = false;

    constructor(
        private syslogService: SyslogService,
        private toastr: ToastrService,
        private modalService: NgbModal
    ) {
        const wordWrapPreference = localStorage.getItem('live-syslog-word-wrap');
        if (wordWrapPreference) {
            this.wordWrap = JSON.parse(wordWrapPreference);
        }
    }

    ngAfterViewChecked() {
        if (this.shouldScrollToBottom) {
            this.scrollToBottom();
            this.shouldScrollToBottom = false;
        }
    }

    openHelp() {
        this.modalService.open(LiveSyslogHelpComponent);
    }

    onWordWrapChange() {
        localStorage.setItem('live-syslog-word-wrap', JSON.stringify(this.wordWrap));
    }

    async loadLogs() {
        if (!this.serialNumber) return;
        this.loading = true;
        try {
            const logs = await this.syslogService.getLiveSyslogs(this.serialNumber, this.lineCount);
            this.logs = logs.reverse();
            this.shouldScrollToBottom = true;
        } catch (error) {
            this.toastr.error('Failed to load logs');
        } finally {
            this.loading = false;
        }
    }

    async requestNewLog() {
        if (!this.serialNumber || this.cooldown > 0) return;
        this.requesting = true;
        try {
            const requestIds = await this.syslogService.requestSyslog(this.serialNumber, 'dcc-liveview');
            this.toastr.success('Log requested');

            await this.pollForLogCompletion(requestIds[0]);
        } catch (error) {
            this.toastr.error('Failed to request log');
        } finally {
            this.requesting = false;
        }
    }

    private scrollToBottom(): void {
        try {
            this.viewer.nativeElement.scrollTop = this.viewer.nativeElement.scrollHeight;
        } catch (err) { }
    }

    private pollForLogCompletion(requestId: string): Promise<void> {
      return new Promise((resolve, reject) => {
        let attempts = 0;
        const maxAttempts = 60; // 1 minute at 1-second intervals
        const pollInterval = setInterval(async () => {
          attempts++;
          if (attempts > maxAttempts) {
            clearInterval(pollInterval);
            this.toastr.error('Live log refreshing timed-out. Please try again.');
            reject(new Error('Polling timed out'));
            return;
          }

          try {
            const syslogRequest = await this.syslogService.getSyslog(requestId);
            if (syslogRequest.status === 'COMPLETED') {
              clearInterval(pollInterval);
              this.toastr.success('Log request completed. Loading logs...');
              await this.loadLogs();
              resolve();
            }
          } catch (error) {
            console.error('Error checking log request status:', error);
            // Continue polling even if there's an error checking status
          }
        }, 1000); // Poll every 1 second
      });
    }
}
